// WindowPicker — 窗口天数选择器（7 / 30 / 90） + 可选粒度切换（F61）
// - value / onChange 单向 controlled
// - granularity / onGranularityChange 可选；传入时同排显示粒度分段
// - 7 天固定 day；30 天可切 day/week；90 天可切 day/week/month
// - CLAUDE.md §四：中文标签 whitespace-nowrap；不用 title 做 tooltip

import { useEffect } from 'react';

import {
  granularitiesFor,
  GRANULARITY_LABEL,
  type AnalyticsGranularity,
} from '../../lib/analytics.js';
import { cn } from '../../lib/cn.js';

const OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
  { value: 90, label: '90 天' },
];

interface Props {
  value: number;
  onChange: (next: number) => void;
  granularity?: AnalyticsGranularity;
  onGranularityChange?: (next: AnalyticsGranularity) => void;
}

export function WindowPicker({
  value,
  onChange,
  granularity,
  onGranularityChange,
}: Props) {
  // 当窗口变化导致当前粒度不允许时（如从 90d/month 切回 7d），把粒度归位到 'day'
  useEffect(() => {
    if (!granularity || !onGranularityChange) return;
    const allowed = granularitiesFor(value);
    if (!allowed.includes(granularity)) onGranularityChange('day');
  }, [value, granularity, onGranularityChange]);

  const allowedGrans = granularitiesFor(value);
  const showGranularity =
    granularity != null && onGranularityChange != null && allowedGrans.length > 1;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="tablist"
        aria-label="统计窗口"
        className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 text-xs dark:border-slate-700 dark:bg-slate-900"
      >
        {OPTIONS.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                'inline-flex h-7 items-center rounded-md px-3 font-medium whitespace-nowrap transition-colors',
                active
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {showGranularity && granularity && onGranularityChange ? (
        <div
          role="tablist"
          aria-label="统计粒度"
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 text-xs dark:border-slate-700 dark:bg-slate-900"
        >
          {allowedGrans.map((g) => {
            const active = g === granularity;
            return (
              <button
                key={g}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onGranularityChange(g)}
                className={cn(
                  'inline-flex h-7 items-center rounded-md px-2.5 font-medium whitespace-nowrap transition-colors',
                  active
                    ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                {GRANULARITY_LABEL[g]}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
